<template>
	<div class="entity-reports-wrapper" :inert="permissionsLoading || undefined" :aria-busy="permissionsLoading || undefined">
		<div v-if="permissionsLoading" role="status" aria-label="Loading report permissions">
			<div class="text-h6">Sparky's Reports</div>
			<v-skeleton-loader type="list-item-two-line" />
		</div>
		<!-- LSP-17773: show Sparky's Reports to a signed-in EXTERNAL shared viewer too (previously
		     gated to same-org). External access requires a VERIFIED share: hasShareAccess is true only
		     when the parent resolved a matching ProjectShare row (decideProjectAccess → "share" origin),
		     never from a URL alone. They have view-only access to the project's content, so the AI
		     report of that content is consistent; run/create stays hidden via showEditorActions.
		     Data still comes from Scribe with the user's token — whether Scribe authorizes a cross-org
		     token for these analyses is a Scribe-side dependency; until confirmed, externals may just
		     see the empty state (see the note if the list is empty). -->
		<!-- LSP-17976: Scribe archived this user for the organization, so the list below comes back empty
		     (or failed). Say so instead of "No reports yet" or the owning-organization fallback. Reports
		     that did load (e.g. from the owning organization of a shared project) always win, so the
		     notice waits for the list to settle — unless the list already failed, which leaves no reports
		     to wait for and would otherwise fall through to the owning-organization fallback. A viewer with
		     no access to this entity keeps that fallback: it, not their own archived access, is the reason. -->
		<div
			v-if="
				isAuthenticated &&
				(isSameOrganization || hasShareAccess) &&
				scribeAccess.isArchived.value &&
				!hasAnyReportRows &&
				(loadFailed || !isLoading)
			"
			class="ma-4">
			<ScribeAccessArchivedNotice />
		</div>
		<div v-else-if="!loadFailed && isAuthenticated && (isSameOrganization || hasShareAccess)" class="d-flex flex-column ga-4">
			<div class="d-flex align-center justify-space-between ga-2">
				<div class="d-flex flex-column">
					<div class="text-h6">Sparky's Reports</div>
				</div>
				<v-btn v-if="showEditorActions" color="primary" variant="outlined" rounded="xl" @click="emitCreate">Run a Spark</v-btn>
			</div>

			<div v-if="isLoading" class="d-flex flex-column ga-2">
				<div class="text-body-2 text-medium-emphasis">Loading reports...</div>
				<v-progress-linear indeterminate color="primary" rounded />
			</div>

			<!-- LSP-20159: the create request waits on Scribe for seconds; acknowledge the run immediately. -->
			<div v-if="visibleStartingReports.length > 0" class="d-flex flex-column ga-2" role="status">
				<div class="text-body-2 text-medium-emphasis">Starting your report…</div>
				<v-progress-linear indeterminate color="primary" rounded />
			</div>

			<template v-if="display.smAndDown.value">
				<div v-if="activeRows.length > 0" class="d-flex flex-column ga-2">
					<div class="text-overline text-medium-emphasis">RUNNING</div>
					<SparkyReportCard
						v-for="item in activeRows"
						:key="item.id"
						:item="item"
						variant="active"
						:show-editor-actions="showEditorActions"
						:cancelling-ids="cancellingIds"
						:is-cancelling="isCancelling"
						:cancelling-schedule-ids="cancellingScheduleIds"
						:finalizing-ids="finalizingIds"
						:is-report-viewable="isReportViewable"
						:is-real-time-analysis="isRealTimeAnalysis"
						:has-partial-result="hasPartialResult"
						:has-excluded-documents="hasExcludedDocuments"
						:is-initial-report-ready="isInitialReportReady"
						:get-in-progress-label="getInProgressLabel"
						:get-elapsed-seconds="getElapsedSeconds"
						@view="view"
						@remove="remove"
						@cancel="handleCancel"
						@cancel-schedule="handleCardCancelSchedule"
						@finalize="handleFinalize" />
				</div>
				<div v-if="completedRows.length > 0" class="d-flex flex-column ga-2">
					<div class="text-overline text-medium-emphasis">
						COMPLETED <span class="ml-1">{{ completedRows.length }}</span>
					</div>
					<SparkyReportCard
						v-for="item in completedRows"
						:key="item.id"
						:item="item"
						variant="completed"
						:sharing-disabled="isCrossOrgProjectViewer"
						:show-editor-actions="showEditorActions"
						:cancelling-ids="cancellingIds"
						:is-cancelling="isCancelling"
						:cancelling-schedule-ids="cancellingScheduleIds"
						:finalizing-ids="finalizingIds"
						:is-report-viewable="isReportViewable"
						:is-real-time-analysis="isRealTimeAnalysis"
						:has-partial-result="hasPartialResult"
						:has-excluded-documents="hasExcludedDocuments"
						:is-initial-report-ready="isInitialReportReady"
						:get-in-progress-label="getInProgressLabel"
						:get-elapsed-seconds="getElapsedSeconds"
						@view="view"
						@remove="remove"
						@share="shareReport(item)"
						@cancel="handleCancel" />
				</div>
			</template>
			<template v-else>
				<div v-if="activeRows.length > 0">
					<div class="text-subtitle-2 font-weight-bold text-medium-emphasis mb-1">Scheduled & Running</div>
					<v-data-table
						:headers="activeHeaders"
						:items="activeRows"
						:items-per-page="-1"
						class="entity-reports pl-2"
						density="compact">
						<template #item.name="{ item }">
							<div
								class="d-flex align-center w-100 report-name-cell"
								:class="{
									'cursor-pointer': isReportViewable(item),
									'cursor-default': !isReportViewable(item)
								}"
								:title="item.name"
								@click.stop="view(item)">
								<span class="text-truncate report-name-text">{{ item.name }}</span>
								<v-chip
									v-if="item.isScheduled"
									size="x-small"
									variant="flat"
									class="ml-1 flex-shrink-0 status-chip-scheduled"
									>{{ SCHEDULED_WAITING_FOR_MEDIA_LABEL }}</v-chip
								>
								<v-chip v-else size="x-small" variant="flat" class="ml-1 flex-shrink-0 status-chip-processing">{{
									getInProgressLabel(item.status)
								}}</v-chip>
								<!-- LSP-16433: also surface the omission on the running row, not only the completed one. It
								appears once the browser has stamped the analysisId on the ledger (best-effort, just after
								dispatch) and the next list poll merges it in — i.e. shortly into the run, not instantly. -->
								<v-tooltip v-if="hasExcludedDocuments(item)" location="bottom">
									<template #activator="{ props: tooltipProps }">
										<v-chip
											v-bind="tooltipProps"
											size="x-small"
											color="warning"
											variant="tonal"
											class="ml-1 flex-shrink-0"
											>Warning</v-chip
										>
									</template>
									<span>{{ warningTooltip(item) }}</span>
								</v-tooltip>
							</div>
						</template>
						<template v-if="entityType === 'project'" #item.owner="{ item }">
							<div
								v-if="item.owner"
								class="text-truncate w-100"
								:class="{
									'cursor-pointer': isReportViewable(item),
									'cursor-default': !isReportViewable(item)
								}"
								:title="item.owner"
								@click.stop="view(item)">
								{{ item.owner }}
							</div>
						</template>
						<template #item.elapsed="{ item }">
							<div
								v-if="item.status === AnalysisStatus.WAITING_FOR_INPUT"
								class="w-100 text-center"
								:class="{
									'cursor-pointer': isReportViewable(item),
									'cursor-default': !isReportViewable(item)
								}"
								@click.stop="view(item)">
								-
							</div>
							<div
								v-else-if="!item.isScheduled"
								class="d-flex align-center ga-1 w-100 justify-center"
								:class="{
									'cursor-pointer': isReportViewable(item),
									'cursor-default': !isReportViewable(item)
								}"
								@click.stop="view(item)">
								<v-icon size="14" class="text-medium-emphasis">icon-clock-stroke</v-icon>
								<span>{{ formatDurationShort(getElapsedSeconds(item)) }}</span>
							</div>
						</template>
						<template #item.actions="{ item }">
							<div class="d-flex align-center justify-center ga-2 w-100">
								<template v-if="item.isScheduled">
									<v-tooltip v-if="showEditorActions" location="bottom">
										<template #activator="{ props: tooltipProps }">
											<v-btn
												data-test-id="report-cancel-button"
												icon
												variant="plain"
												density="compact"
												aria-label="Cancel planned report"
												:loading="
													!!item._scheduledAnalysis?.id && cancellingScheduleIds.has(item._scheduledAnalysis.id)
												"
												v-bind="tooltipProps"
												@click.stop="item._scheduledAnalysis && handleCancelSchedule(item._scheduledAnalysis)">
												<v-icon color="error">icon-close-circle-stroke</v-icon>
											</v-btn>
										</template>
										<span>Cancel this planned report</span>
									</v-tooltip>
								</template>
								<template v-else-if="isReportViewable(item)">
									<v-btn icon title="View" variant="plain" density="compact" @click.stop="view(item)">
										<v-icon>icon-eye-stroke</v-icon>
									</v-btn>
								</template>
								<!-- LSP-20160: a cancelling row keeps its Cancel control, disabled and labelled
								     "Cancelling…" (aria-label + tooltip). This disables an in-progress action, not a
								     form on validity. The tooltip activator is the span so the explanation still
								     shows on the disabled button, same as ReportsPage.vue. -->
								<template v-else-if="showEditorActions && hasCancelControl(item.status) && !isRealTimeAnalysis(item)">
									<v-tooltip location="bottom">
										<template #activator="{ props: tooltipProps }">
											<span v-bind="tooltipProps" class="d-inline-flex" @click.stop>
												<v-btn
													data-test-id="report-cancel-button"
													icon
													variant="plain"
													density="compact"
													:aria-label="cancelControlLabel(item)"
													:disabled="isCancelling(item)"
													:loading="cancellingIds.has(item.id)"
													@click.stop="handleCancel(item)">
													<v-icon color="error">icon-close-circle-stroke</v-icon>
												</v-btn>
											</span>
										</template>
										<span>{{ isCancelling(item) ? CANCELLING_LABEL : "Stop Sparky from fetching this report" }}</span>
									</v-tooltip>
								</template>
								<template v-else-if="showEditorActions && isRealTimeAnalysis(item)">
									<v-tooltip location="bottom">
										<template #activator="{ props: tooltipProps }">
											<v-btn
												icon
												variant="plain"
												density="compact"
												:loading="finalizingIds.has(item.id)"
												v-bind="tooltipProps"
												@click.stop="handleFinalize(item)">
												<v-icon color="primary" size="24">mdi-check-circle-outline</v-icon>
											</v-btn>
										</template>
										<span>Finalize this real-time report</span>
									</v-tooltip>
								</template>
							</div>
						</template>
						<template #bottom></template>
					</v-data-table>
				</div>

				<div v-if="completedRows.length > 0">
					<div class="text-subtitle-2 font-weight-bold text-medium-emphasis mb-1">Completed reports</div>
					<v-data-table
						:headers="completedHeaders"
						:items="completedRows"
						:items-per-page="-1"
						class="entity-reports pl-2"
						density="compact">
						<template #item.name="{ item }">
							<div
								class="d-flex align-center w-100 report-name-cell"
								:class="{
									'cursor-pointer': isReportViewable(item),
									'cursor-default': !isReportViewable(item)
								}"
								:title="item.name"
								@click.stop="view(item)">
								<span class="text-truncate report-name-text">{{ item.name }}</span>
								<v-tooltip v-if="hasPartialResult(item) || hasExcludedDocuments(item)" location="bottom">
									<template #activator="{ props: tooltipProps }">
										<v-chip
											v-bind="tooltipProps"
											size="x-small"
											color="warning"
											variant="tonal"
											class="ml-1 flex-shrink-0"
											>Warning</v-chip
										>
									</template>
									<span>{{ warningTooltip(item) }}</span>
								</v-tooltip>
								<v-chip
									v-else-if="item.status === AnalysisStatus.FAILED"
									size="x-small"
									color="error"
									variant="tonal"
									class="ml-1 flex-shrink-0"
									>Failed</v-chip
								>
								<v-chip
									v-else-if="item.status === AnalysisStatus.WAITING_FOR_INPUT"
									size="x-small"
									color="teal"
									variant="tonal"
									class="ml-1 flex-shrink-0"
									>Waiting for Input</v-chip
								>
								<v-chip
									v-else-if="isInitialReportReady(item)"
									size="x-small"
									color="teal"
									variant="tonal"
									class="ml-1 flex-shrink-0"
									>Initial Report Ready</v-chip
								>
							</div>
						</template>
						<template v-if="entityType === 'project'" #item.owner="{ item }">
							<div
								v-if="item.owner"
								class="text-truncate w-100"
								:class="{
									'cursor-pointer': isReportViewable(item),
									'cursor-default': !isReportViewable(item)
								}"
								:title="item.owner"
								@click.stop="view(item)">
								{{ item.owner }}
							</div>
						</template>
						<template #item.createdAt="{ item }">
							<div
								class="d-flex flex-column text-center w-100"
								:class="{
									'cursor-pointer': isReportViewable(item),
									'cursor-default': !isReportViewable(item)
								}"
								@click.stop="view(item)">
								<div class="date-cell">{{ timezones.formatDateOnlyShort(item.createdAt) }}</div>
								<div class="date-cell">{{ timezones.formatTimeOnly(item.createdAt) }}</div>
							</div>
						</template>
						<template #item.actions="{ item }">
							<div class="d-flex align-center justify-center ga-2 w-100">
								<v-btn
									v-if="isReportViewable(item)"
									icon
									title="View"
									variant="plain"
									density="compact"
									@click.stop="view(item)">
									<v-icon>icon-eye-stroke</v-icon>
								</v-btn>
								<v-tooltip v-if="isReportViewable(item)" location="top"
									:text="isCrossOrgProjectViewer ? SHARE_PERMISSION_MESSAGE : 'Share'">
									<template #activator="{ props: tooltipProps }">
										<span v-bind="tooltipProps" class="d-inline-flex" :tabindex="isCrossOrgProjectViewer ? 0 : undefined" @click.stop>
											<v-btn data-test-id="report-share-button" icon variant="plain" density="compact"
												aria-label="Share" :disabled="isCrossOrgProjectViewer"
												:aria-description="isCrossOrgProjectViewer ? SHARE_PERMISSION_MESSAGE : undefined"
												@click.stop="shareReport(item)">
												<v-icon>icon-share-contact-stroke</v-icon>
											</v-btn>
										</span>
									</template>
								</v-tooltip>
								<v-tooltip v-if="item.status === AnalysisStatus.FAILED && !hasPartialResult(item)" location="bottom">
									<template #activator="{ props: tooltipProps }">
										<v-icon v-bind="tooltipProps" size="18">mdi-alert-circle-outline</v-icon>
									</template>
									<span
										>This report has failed, support has been notified and is looking into it. Please click Sparky to
										run a new report.</span
									>
								</v-tooltip>
								<!-- LSP-20160: disabled while cancelling (an in-progress action, not validity gating). -->
								<v-btn
									v-if="showEditorActions && hasCancelControl(item.status)"
									data-test-id="report-cancel-button"
									icon
									:title="isCancelling(item) ? CANCELLING_LABEL : 'Cancel'"
									:aria-label="isCancelling(item) ? CANCELLING_LABEL : 'Cancel'"
									variant="plain"
									density="compact"
									:disabled="isCancelling(item)"
									:loading="cancellingIds.has(item.id)"
									@click.stop="handleCancel(item)">
									<v-icon color="error">icon-close-circle-stroke</v-icon>
								</v-btn>
								<v-btn
									v-else-if="showEditorActions"
									data-test-id="report-delete-button"
									icon
									title="Delete"
									variant="plain"
									density="compact"
									@click.stop="remove(item)">
									<v-icon color="error">icon-trash-full-stroke</v-icon>
								</v-btn>
							</div>
						</template>
						<template #bottom></template>
					</v-data-table>
				</div>
			</template>

			<div
				v-if="
					!loading &&
					!loadingScheduledAnalyses &&
					activeRows.length === 0 &&
					completedRows.length === 0 &&
					visibleStartingReports.length === 0
				"
				class="d-flex flex-column align-center justify-center text-center ga-2 py-8">
				<v-icon size="44" color="primary">mdi-view-grid</v-icon>
				<div class="text-h6">No reports yet</div>
				<div class="text-body-2 text-medium-emphasis">When you run a Spark, its report will show up here.</div>
				<v-btn v-if="showEditorActions" color="primary" variant="flat" rounded="xl" class="mt-2" @click="emitCreate"
					>Run your first Spark</v-btn
				>
			</div>
		</div>

		<div v-else class="ma-4 d-flex flex-column align-center justify-center">
			<v-icon color="black" style="font-size: 80px">icon-form</v-icon>
			<div class="text-h6">Looking for Sparky's Reports?</div>
			<div class="text-subtitle-1">
				Sparky's Reports are only accessible if you're signed in to the organization that owns the project, or the project was
				shared with you.
			</div>
		</div>

		<ScribeReportDialog
			v-model="reportVisible"
			:analysis="selectedAnalysis"
			:loading="loadingAnalysis"
			:conversation-type="selectedConversationType"
			:conversation-status="selectedConversationStatus"
			:excluded-documents="selectedExcludedDocuments"
			:fallback-external-id="entity?.id"
			:fallback-external-type="entityType"
			:is-cross-org-project-viewer="isCrossOrgProjectViewer"
			@report-updated="handleReportUpdated"
			@update:model-value="handleDialogInput" />

		<ShareReportDialog
			v-if="shareTarget"
			:dialog-visible="shareDialogVisible"
			:analysis-id="shareTarget.analysisId"
			:external-type="shareTarget.externalType"
			:external-id="shareTarget.externalId"
			:report-title="shareTarget.reportTitle"
			@cancel="shareDialogVisible = false" />
	</div>
</template>

<script setup lang="ts">
	import { onMounted, onUnmounted, ref, computed, reactive, watch, getCurrentInstance, nextTick } from "vue";
	import { TabIds } from "@/enums/TabIds";
	import { useScribeStore } from "@/stores/scribe";
	import type { Conversation } from "@/types/Conversation";
	import type { Project } from "@/types/Project";
	import type { EntityType } from "@/types/EntityType";
	import { AnalysisResult, AnalysisStatus } from "@/types/scribe/Analysis";
	import type { AnalysisStatusResponseData } from "@/types/scribe";
	import { SCHEDULED_WAITING_FOR_MEDIA_LABEL } from "./sparkyReportTypes";
	import type { RowItem, ScheduledAnalysisItem, ExcludedDocument } from "./sparkyReportTypes";
	import type { ComponentProxy } from "@/types/ConfirmService";
	// deleteAnalysis (scribe-direct) is intentionally NOT imported: spark delete now routes through
	// contact-api `api.deleteSpark`, which deletes the scribe analysis AND cleans the AnalysisSchedule row.
	import { getAnalyses, getAnalysis, finalizeRealtimeAnalysis } from "@/api/scribe";
	import api from "@/api";
	import ScribeReportDialog from "@/components/scribe/ScribeReportDialog.vue";
	import ScribeAccessArchivedNotice from "@/components/scribe/ScribeAccessArchivedNotice.vue";
	import { useScribeAccess } from "@/composables/useScribeAccess";
	import ShareReportDialog from "@/components/ShareReportDialog.vue";
	import SparkyReportCard from "./SparkyReportCard.vue";
	import {
		PENDING_REPORT_DISPLAY_NAME,
		reportDisplayName,
		statusOnlyReportDisplayName,
		stripInternalReportPrefix
	} from "@/components/scribe/utils/reportName";
	import { useToast } from "vue-toastification";
	import { notifyReportOpenFailed, notifyReportUnavailable } from "@/helpers/reportView";
	import timezones from "@/helpers/timezones";
	import { useRoute, useRouter } from "vue-router";
	import { useDisplay } from "vuetify";
	import { formatDurationShort } from "@/helpers/time";
	import { doesRowMatchSchedule } from "@/helpers/entityReportsDedup";
	import { couldNot, SHARE_PERMISSION_MESSAGE } from "@/helpers/userMessages";
	import { readFreshEntity } from "@/helpers/freshEntityRetry";
	import {
		CANCELLING_LABEL,
		hasCancelControl,
		isCancellableAnalysisStatus,
		isCancellingAnalysisStatus,
		isWaitingForDeferredMedia as isAnalysisWaitingForDeferredMedia,
		normalizeAnalysisStatus
	} from "@/components/scribe/utils/analysisStatus";

	const props = defineProps<{
		entity: Conversation | Project;
		entityType: EntityType;
		subEntityId?: string;
		additionalEntities?: Partial<Conversation>[];
		isViewOnly?: boolean;
		isAuthenticated?: boolean;
		hasFreeTrialEnded?: boolean;
		isSameOrganization?: boolean;
		permissionsLoading?: boolean;
		// LSP-17773: true only when the viewer's access was resolved from a verified ProjectShare row
		// (decideProjectAccess returned "share" origin) — lets an external shared viewer see the list.
		hasShareAccess?: boolean;
	}>();

	// LSP-18409 follow-up: a shared PROJECT report opened by a viewer/editor in a DIFFERENT org than the
	// owner must use the legacy in-app viewer — the V2 embed iframe can't authenticate the caller's token
	// against the owner's Scribe tenant and times out. Only this narrow case flips the dialog; owner and
	// same-org viewers keep the V2 iframe unchanged.
	const isCrossOrgProjectViewer = computed(
		() => props.entityType === "project" && props.isSameOrganization === false && props.hasShareAccess === true
	);

	const IN_PROGRESS_STATUSES = new Set([
		AnalysisStatus.QUEUED,
		AnalysisStatus.PENDING,
		AnalysisStatus.RUNNING,
		AnalysisStatus.WAITING_FOR_INPUT,
		AnalysisStatus.CANCELLING
	]);

	function isInProgress(status: AnalysisStatus | string | null | undefined): boolean {
		const normalizedStatus = normalizeAnalysisStatus(status);
		return !!normalizedStatus && IN_PROGRESS_STATUSES.has(normalizedStatus);
	}

	function isInitialReportReady(item: RowItem): boolean {
		// Check if the media step is complete (mediaCompleteAt is set) while analysis is still running.
		// WAITING_FOR_INPUT deliberately excluded: media processing necessarily finishes before an
		// analysis can even reach WAITING_FOR_INPUT, so mediaCompleteAt is truthy for nearly every such
		// report — including it here bucketed WAITING_FOR_INPUT into "Completed" (isReportViewable/the
		// Scheduled & Running split both treat isInitialReportReady as "done"), even though the report
		// still needs the user's input before it's actually finished.
		return item.status === AnalysisStatus.RUNNING && !!item.mediaCompleteAt;
	}

	function hasPartialResult(item: RowItem): boolean {
		// Check if a FAILED analysis has a viewable media result (mediaCompleteAt was set before failure)
		return item.status === AnalysisStatus.FAILED && !!item.mediaCompleteAt;
	}

	// LSP-16433: orthogonal to hasPartialResult — a (possibly COMPLETED) report that omitted an oversized
	// document. Drives the same Warning chip without changing hasPartialResult's FAILED-only semantics.
	function hasExcludedDocuments(item: RowItem): boolean {
		return (item.excludedDocuments?.length ?? 0) > 0;
	}

	// Tooltip copy for the shared Warning chip. Preserves the original precedence: the FAILED-with-media
	// follow-up message wins when both apply (they can co-occur on a FAILED report that also dropped a file).
	function warningTooltip(item: RowItem): string {
		if (hasPartialResult(item)) {
			return "This Spark was able to create your analysis, but failed on follow up steps. The LiveSwitch support team has been notified, and is looking into it.";
		}
		return `${item.excludedDocuments?.length} document(s) were too large to include and were omitted from this report. Open the report for details.`;
	}

	function isReportViewable(item: RowItem): boolean {
		return (
			item.status === AnalysisStatus.COMPLETED ||
			item.status === AnalysisStatus.WAITING_FOR_INPUT ||
			isInitialReportReady(item) ||
			hasPartialResult(item)
		);
	}

	function isRealTimeAnalysis(item: RowItem): boolean {
		return item.isRealTime === true;
	}

	// LSP-20160: the single "is this row cancelling" predicate for the table and the cards — the cancel
	// request is in flight (directly, or through the schedule-aware path for a running row that still
	// carries its planned schedule), or Scribe acknowledged it (the row is CANCELLING). Either way Cancel
	// stays visible but disabled as "Cancelling…" and handleCancel no-ops.
	function isScheduleCancelInFlight(item: RowItem): boolean {
		return !!item._scheduledAnalysis?.id && cancellingScheduleIds.has(item._scheduledAnalysis.id);
	}

	function isCancelling(item: RowItem): boolean {
		return cancellingIds.has(item.id) || isScheduleCancelInFlight(item) || isCancellingAnalysisStatus(item.status);
	}

	function cancelControlLabel(item: RowItem): string {
		return isCancelling(item) ? CANCELLING_LABEL : "Cancel report";
	}

	const instance = getCurrentInstance();
	const emit = defineEmits<{ (e: "create"): void }>();
	const toast = useToast();
	const route = useRoute();
	const router = useRouter();
	const rows = ref<RowItem[]>([]);
	// LSP-16433: oversized-document omissions fetched separately from contact-api and merged onto report rows
	// by Scribe analysisId (=== report row id). analysisId is the only reliable bridge — Scribe results echo
	// neither our packId nor a top-level externalId — so the browser stamps it onto the ledger row after
	// dispatch. Kept out of `scheduledAnalyses` so it never generates phantom PENDING rows.
	const excludedDocsByAnalysisId = ref<Map<string, ExcludedDocument[]>>(new Map());
	const loading = ref(false);
	const loadFailed = ref<boolean>(false);
	const cancellingIds = reactive(new Set<string>());
	// LSP-20160: ids whose "Cancel Report?" confirm is open. Not reactive: it only blocks a re-entrant
	// handleCancel while the dialog is up and never drives rendering.
	const confirmingCancelIds = new Set<string>();
	const finalizingIds = reactive(new Set<string>());
	const removedAnalysisIds = reactive(new Set<string>());

	const scheduledAnalyses = ref<ScheduledAnalysisItem[]>([]);
	const scheduledAnalysesRequestId = ref(0);
	// LSP-19608: readFreshEntity's isSuperseded check has no other way to learn the component went away —
	// neither requestId nor props.entity?.id changes on unmount — so a sleeping retry would otherwise keep
	// firing requests (and writing refs) for up to ~3.5s after the panel is destroyed.
	let isUnmounted = false;
	const loadingScheduledAnalyses = ref(false);
	const cancellingScheduleIds = reactive(new Set<string>());
	const loadRequestId = ref(0);

	const reportVisible = ref(false);
	const selectedAnalysis = ref<AnalysisResult | null>(null);
	// LSP-16433: omissions for the report currently open in the viewer (passed through to the notice).
	const selectedExcludedDocuments = ref<ExcludedDocument[]>([]);
	const loadingAnalysis = ref(false);
	const isLoading = computed(() => loading.value || loadingScheduledAnalyses.value);

	type ShareTarget = {
		analysisId: string;
		externalType: string;
		externalId: string;
		reportTitle?: string;
	};

	const shareDialogVisible = ref(false);
	const shareTarget = ref<ShareTarget | null>(null);

	function shareReport(item: RowItem) {
		if (props.permissionsLoading || isCrossOrgProjectViewer.value) return;
		// externalType/externalId are the authoritative values scribe assigned to the analysis,
		// now carried on every row by mapToRows + mergeStoreAnalyses (both sourced from the
		// analysis's own externalType/externalId — never inferred). The contact-api share-notify
		// endpoint uses them to resolve the report's parent project for its authz check. Fall back
		// to the loaded analysis, then the component's own entity, only if a row somehow lacks them.
		const resolvedExternalType = item.externalType ?? item._analysis?.externalType ?? (props.entityType as string);
		const resolvedExternalId = item.externalId ?? item._analysis?.externalId ?? (props.entity.id as string);
		shareTarget.value = {
			analysisId: item.id,
			externalType: resolvedExternalType,
			externalId: resolvedExternalId,
			// Prefer the analysis title, falling back to the display filename — same precedence the
			// report viewer uses, so a report's share email shows the same name from either entry point.
			//
			// LSP-19657: except the pending PLACEHOLDER. A status-only row (one the status poll knows
			// about but the results endpoint has not surfaced yet) can be COMPLETED, and therefore
			// shareable via isReportViewable, while its `name` is still "Report in progress". That string
			// is a display affordance for the table cell, not the report's identity, so it must not go out
			// as the name in a share email. notifyReportShare takes reportName as optional, so sending
			// nothing is the supported way to say "no name known".
			reportTitle: item.title || (item.name === PENDING_REPORT_DISPLAY_NAME ? undefined : item.name)
		};
		shareDialogVisible.value = true;
	}

	// Show extra actions.
	const showEditorActions = computed(() => !!props.isAuthenticated && !props.isViewOnly && !props.hasFreeTrialEnded);
	const additionalEntityIds = computed(() => (props.additionalEntities ?? []).map((entity) => entity.id).filter(Boolean) as string[]);
	const additionalEntityIdsDigest = computed(() => additionalEntityIds.value.join(","));

	const selectedConversationType = computed(() => {
		if (props.entityType === "conversation") return (props.entity as Conversation).type;
		const conversationId = selectedAnalysis.value?.externalId ?? selectedAnalysis.value?.metadata?.conversation?.id;
		if (!conversationId) return undefined;
		return props.additionalEntities?.find((entity) => entity.id === conversationId)?.type;
	});

	const selectedConversationStatus = computed(() => {
		if (props.entityType === "conversation") return (props.entity as Conversation).status;
		const conversationId = selectedAnalysis.value?.externalId ?? selectedAnalysis.value?.metadata?.conversation?.id;
		if (!conversationId) return undefined;
		return props.additionalEntities?.find((entity) => entity.id === conversationId)?.status;
	});

	const display = useDisplay();
	const scribeAccess = useScribeAccess();

	// Local mirror of Vuetify 3's DataTableHeader shape — the public type is not
	// re-exported from the runtime entry, so we encode the fields we actually
	// use. Keeps strong typing across conditional pushes (no more
	// Array<Record<string, unknown>>).
	type ReportTableHeader = {
		title: string;
		key: string;
		sortable?: boolean;
		align?: "start" | "center" | "end";
		width?: string;
	};

	// LSP-15915: xs viewports render cards instead of the table (see <template>
	// above), so the table branch only needs to handle sm and up. Keep Session
	// visible across sm/md/desktop — dropping it there is the band-aid this PR
	// is meant to replace with cards.
	const activeHeaders = computed<ReportTableHeader[]>(() => {
		const headers: ReportTableHeader[] = [{ title: "Name", key: "name", sortable: false }];
		if (props.entityType === "project" && !display.xs.value) {
			headers.push({ title: "Session", key: "owner", sortable: false, align: "center", width: "130px" });
		}
		headers.push({ title: "Elapsed", key: "elapsed", sortable: false, align: "center", width: "100px" });
		headers.push({ title: "", key: "actions", sortable: false, align: "center", width: "60px" });
		return headers;
	});

	const completedHeaders = computed<ReportTableHeader[]>(() => {
		const headers: ReportTableHeader[] = [{ title: "Name", key: "name", sortable: false }];
		if (props.entityType === "project" && !display.xs.value) {
			headers.push({ title: "Session", key: "owner", sortable: false, align: "center", width: "130px" });
		}
		headers.push({ title: "Created At", key: "createdAt", sortable: false, align: "center", width: "100px" });
		// 110px: fits the View + Share + Delete compact icon buttons without clipping.
		headers.push({ title: "", key: "actions", sortable: false, align: "center", width: "110px" });
		return headers;
	});

	function isWaitingForDeferredMedia(row: RowItem): boolean {
		return isAnalysisWaitingForDeferredMedia(row.status, row.deferredMedia);
	}

	function applyScribeScheduledState(row: RowItem): RowItem {
		return {
			...row,
			isScheduled: isWaitingForDeferredMedia(row)
		};
	}

	const combinedRows = computed<RowItem[]>(() => {
		const matchedScheduleIds = new Set<string>();
		const analysisRows = rows.value.map((row) => {
			// LSP-16433: attach the omission by analysisId (=== row id) — the only reliable bridge, since
			// Scribe results echo neither our packId nor a top-level externalId.
			const excludedDocuments = excludedDocsByAnalysisId.value.get(row.id);
			const withExcluded: RowItem = excludedDocuments?.length ? { ...row, excludedDocuments } : row;

			const matchingSchedule = scheduledAnalyses.value.find(
				(schedule) => !matchedScheduleIds.has(schedule.id) && doesRowMatchSchedule(row, schedule)
			);

			if (!matchingSchedule) {
				return applyScribeScheduledState(withExcluded);
			}

			matchedScheduleIds.add(matchingSchedule.id);
			return applyScribeScheduledState(mergeRowWithSchedule(withExcluded, matchingSchedule));
		});

		const scheduledRows: RowItem[] = scheduledAnalyses.value
			.filter((schedule) => !matchedScheduleIds.has(schedule.id))
			.map((schedule) => {
				const isQueued = schedule.status?.toLowerCase() === "queued";
				return {
					id: schedule.id,
					name: getScheduledDisplayName(schedule),
					createdAt: schedule.createdAt,
					status: isQueued ? AnalysisStatus.PENDING : AnalysisStatus.NO_MEDIA,
					owner: schedule.ownerLabel,
					externalId: schedule.conversationId,
					externalType: "conversation",
					excludedDocuments: schedule.excludedDocuments,
					isScheduled: isQueued,
					_scheduledAnalysis: schedule
				};
			});
		return [...scheduledRows, ...analysisRows].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
	});

	const activeRows = computed(() => {
		return combinedRows.value.filter((row) => row.isScheduled || (isInProgress(row.status) && !isInitialReportReady(row)));
	});

	const completedRows = computed(() => {
		return combinedRows.value.filter((row) => !row.isScheduled && (!isInProgress(row.status) || isInitialReportReady(row)));
	});

	const pollableRows = computed(() => {
		return combinedRows.value.filter((row) => row.isScheduled || isInProgress(row.status));
	});

	const now = ref(Date.now());
	let nowInterval: ReturnType<typeof setInterval> | null = null;

	function toDate(value: string | Date | undefined | null): Date | null {
		if (!value) return null;
		const date = value instanceof Date ? value : new Date(value);
		if (Number.isNaN(date.getTime())) return null;
		return date;
	}

	function getInProgressLabel(status: AnalysisStatus): string {
		switch (status) {
			case AnalysisStatus.QUEUED:
			case AnalysisStatus.PENDING:
				return "Queued";
			case AnalysisStatus.RUNNING:
				return "Running";
			case AnalysisStatus.WAITING_FOR_INPUT:
				return "Waiting for Input";
			case AnalysisStatus.CANCELLING:
				return "Cancelling";
			default:
				return "Working";
		}
	}

	function isStoreTerminalOlderThanListRow(row: RowItem, storeAnalysis: AnalysisStatusResponseData, storeStatus: AnalysisStatus) {
		if (!isInProgress(row.status) || isInProgress(storeStatus)) return false;
		const rowUpdatedAt = toDate(row.updatedAt);
		const storeUpdatedAt = toDate(storeAnalysis.updatedAt);
		return !!rowUpdatedAt && !!storeUpdatedAt && storeUpdatedAt.getTime() < rowUpdatedAt.getTime();
	}

	function getElapsedSeconds(item: RowItem): number {
		// For running analyses, use createdAt (not updatedAt which changes on every frame)
		const startAt = item.isScheduled
			? (toDate(item._scheduledAnalysis?.createdAt) ?? toDate(item.createdAt))
			: (item._statusBecameInProgressAt ?? toDate(item.createdAt));
		if (!startAt) return 0;
		return Math.max(0, Math.floor((now.value - startAt.getTime()) / 1000));
	}

	function getConversationOwnerLabel(conversationId: string | undefined): string | undefined {
		if (props.entityType !== "project" || !conversationId) {
			return undefined;
		}

		const conversation = props.additionalEntities?.find((entity) => entity.id === conversationId);

		if (conversation?.number) {
			return String(conversation.number);
		}

		return undefined;
	}

	function getScheduledDisplayName(schedule: ScheduledAnalysisItem): string {
		const packName = stripInternalReportPrefix(schedule.packName).trim();
		const conversationName = schedule.conversationName?.trim();
		if (packName && conversationName) return `${packName} - ${conversationName}`;
		return packName || conversationName || "Planned report";
	}

	function mergeRowWithSchedule(row: RowItem, schedule: ScheduledAnalysisItem): RowItem {
		const scheduledDisplayName = getScheduledDisplayName(schedule);
		const hasScheduledConversationName = !!schedule.conversationName?.trim();
		return {
			...row,
			name: hasScheduledConversationName ? scheduledDisplayName : row.name || scheduledDisplayName,
			owner: row.owner ?? schedule.ownerLabel,
			_scheduledAnalysis: schedule
		};
	}

	function resolveConversationIdForSpark(item: RowItem): string | undefined {
		const externalType = item.externalType ?? item._analysis?.externalType;
		if (externalType === "project") {
			// AI PR review finding (round 4, mirror-image gap the round-1 fix on
			// resolveProjectIdForSpark below missed): Scribe's externalType is ADVISORY and can lie —
			// LSP-15178 observed analyses in prod that carry externalType="project" while externalId
			// is actually THIS SAME conversation's own id (a report run over the conversation's
			// media). The old code unconditionally returned undefined here, sending the row to
			// resolveProjectIdForSpark's advisory-field fallback, which then sent the conversation id
			// as a WRONG projectId hint (404s at resolveProjectForSparkAccess before the ledger-row
			// fallback ever runs). When we're inside a CONVERSATION entity's OWN report view
			// (props.entityType === "conversation"), every row came from
			// getAnalyses("contact", "conversation", props.entity.id) — contact-api's listSparks
			// scopes its Scribe query to exactly this conversation (getScribeAnalysesForConversation)
			// — so props.entity.id is trustworthy for a project-mislabeled row here, regardless of
			// what externalId claims (mirrors resolveProjectIdForSpark's own project-view fix below,
			// and mobile's resolveSparkScope fix). Otherwise (e.g. a project's own view merging in a
			// child conversation's row, the only other place externalType==="project" is reached)
			// there is no screen-level conversation id to fall back to, so this stays undefined as
			// before — resolveProjectIdForSpark's own project-view branch handles that case.
			return props.entityType === "conversation" ? (props.entity?.id as string | undefined) : undefined;
		}

		// A row whose OWN externalType genuinely says "conversation" (or omits it) is left
		// untouched here — trusted first via its own externalId, only falling back to the screen's
		// own conversation id when the row supplies none. This is deliberately NOT overridden by the
		// round-4 fix above: an existing, pre-LSP-20681 test (EntityReports.spec.ts's xs-card
		// "forwards the card's remove emit" case) pins a correctly-typed "conversation" row resolving
		// via its OWN externalId even when it differs from props.entity.id, and nothing in this
		// ticket's scope calls that legitimate case into question.
		return (
			item.externalId ??
			item._analysis?.externalId ??
			item._analysis?.metadata?.conversation?.id ??
			(props.entityType === "conversation" ? (props.entity?.id as string | undefined) : undefined)
		);
	}

	// LSP-20681: when resolveConversationIdForSpark can't name a conversation (a project-tab row, or
	// externalType === "project"), fall back to a projectId hint on the flat route instead of calling
	// it hint-less.
	//
	// AI PR review finding (post-merge, HIGH): Scribe's externalType is ADVISORY and can lie —
	// LSP-15178 observed analyses in prod that carry externalType="project" while externalId is
	// actually a CONVERSATION id (a report run over a conversation's media, merged into the project's
	// aggregate by listProjectSparks — see sparks.ts). Trusting item.externalId as a projectId in that
	// shape sends the WRONG hint; the server 404s at resolveProjectForSparkAccess before the
	// ledger-row fallback ever runs, turning a previously-working delete/cancel into a 404 (violates
	// the plan's own "a ledger row always wins over a hint" invariant, since the hint's OWN
	// entity-resolution step fails first).
	//
	// When we're inside a project entity's OWN report view (props.entityType === "project"), every
	// row came from getProjectSparks(props.entity.id) — which only ever returns THIS project's own
	// analyses plus its own child conversations' analyses (sparks.ts listProjectSparks), never a
	// different project's. So props.entity.id is a trustworthy project id for every row in this view,
	// regardless of what the row's own (Scribe-supplied, can-lie) externalType/externalId claims.
	// Prefer it over the per-item field, which is only used as a last-resort fallback when we are NOT
	// looking at a project's own view (unreached by any current entityType now that
	// resolveConversationIdForSpark's own conversation-view short-circuit above always wins first —
	// kept for forward compatibility, mirroring resolveConversationIdForSpark's fallback ordering).
	function resolveProjectIdForSpark(item: RowItem): string | undefined {
		if (props.entityType === "project" && props.entity?.id) {
			return props.entity.id as string;
		}
		return item.externalId ?? item._analysis?.externalId;
	}

	function cancelSparkForRow(item: RowItem): Promise<{ cancelled?: boolean }> {
		const conversationId = resolveConversationIdForSpark(item);
		if (conversationId) return api.cancelSpark(conversationId, item.id);
		return api.cancelSparkById(item.id, { projectId: resolveProjectIdForSpark(item) });
	}

	function deleteSparkForRow(item: RowItem): Promise<{ cancelled?: boolean }> {
		const conversationId = resolveConversationIdForSpark(item);
		if (conversationId) return api.deleteSpark(conversationId, item.id);
		return api.deleteSparkById(item.id, { projectId: resolveProjectIdForSpark(item) });
	}

	// LSP-18409: the server report aggregate (getProjectSparks) merges viewable Scribe analyses with
	// queued/omission placeholder rows built from AnalysisSchedule. Those placeholder rows carry an
	// `isLegacySchedule` marker; real Scribe analyses never do. Discriminate on the MARKER, NOT on
	// status — a genuinely-queued Scribe analysis has status "QUEUED", so a status check would misfile
	// a real running report as a scheduled placeholder (Bugbot). rows vs scheduledAnalyses live in
	// separate arrays here, so this splits the aggregate between them.
	function isLegacyScheduleItem(item: { isLegacySchedule?: boolean }): boolean {
		return item.isLegacySchedule !== undefined;
	}

	function mapToRows(list: AnalysisResult[]): RowItem[] {
		return (list || []).map((a) => {
			// mediaCompleteAt, isRealTime, and sessionId are on analysisSessions/analysis, accessed via any cast
			const extendedAnalysis = a as any;
			const analysisSessions = extendedAnalysis.analysisSessions as
				| Array<{ mediaCompleteAt?: Date | string | null; sessionId?: string }>
				| undefined;
			const mediaCompleteAt = analysisSessions?.[0]?.mediaCompleteAt ?? null;
			const sessionId = analysisSessions?.[0]?.sessionId;
			const isRealTime = extendedAnalysis.isRealTime === true;
			const status = normalizeAnalysisStatus(a.status) ?? a.status;
			return {
				id: a.id,
				name: reportDisplayName(a),
				title: a.title,
				owner: props.entityType === "project" ? getConversationOwnerLabel(a.externalId ?? a.metadata?.conversation?.id) : undefined,
				createdAt: a.createdAt,
				updatedAt: a.updatedAt,
				status,
				externalId: a.externalId ?? a.metadata?.conversation?.id,
				externalType: a.externalType,
				packId: a.packId,
				isRealTime,
				sessionId,
				mediaCompleteAt,
				deferredMedia: a.deferredMedia === true,
				// LSP-18409 (review F6): the owner-tenant getProjectSparks aggregate merges the omission
				// ledger's excludedDocuments onto each row, so carry it through for the chip/notice.
				excludedDocuments: extendedAnalysis.excludedDocuments,
				_analysis: a
			};
		});
	}

	function mergeStoreAnalyses() {
		const existingIds = new Set(rows.value.map((r) => r.id));
		for (const sa of scribeStore.currentAnalyses) {
			if (removedAnalysisIds.has(sa.id)) {
				scribeStore.removeAnalysis(sa.id);
				continue;
			}
			if (!existingIds.has(sa.id)) {
				// Don't add a brand-new analysis that the store already reports as cancelled.
				if (sa.status === AnalysisStatus.CANCELLED) continue;
				// Analysis from status endpoint not in results — add it (in-progress, failed, etc.)
				rows.value.unshift({
					id: sa.id,
					// LSP-19657: rows in "Scheduled & Running" are built from the status-poll payload
					// (GET /analyses/status/external/contact/project/:id), which carries only
					// {id, status, isRealTime, createdAt, updatedAt, deferredMedia} — no title. getDisplayFileName's
					// last resort is `timezones.formatDate(createdAt)`, so the Name column showed a raw
					// timestamp ("09/04/2026 11:12 PM PDT") for a project-scoped in-progress report. That
					// fallback is right for real FILES (a file with no title has a meaningful upload time) and
					// is left alone; it is wrong for a report, whose name is simply not known yet.
					//
					// A pending label is the honest answer for the interval before Scribe's richer results
					// endpoint surfaces the row: mergeStoreAnalyses only ADDS ids the results endpoint has not
					// returned, and mapToRows replaces the row with the real title as soon as it does.
					name: statusOnlyReportDisplayName(sa),
					title: sa.title,
					// LSP-15648: a child report can surface from the status store before the list endpoint
					// returns a row for it. Derive the Session owner from the status entry's externalId, which
					// AnalysisStatusResponseData always carries — the metadata.conversation fallback mapToRows
					// uses is unnecessary here because this row is built from status-endpoint data.
					owner: props.entityType === "project" ? getConversationOwnerLabel(sa.externalId) : undefined,
					createdAt: sa.createdAt,
					updatedAt: sa.updatedAt,
					status: normalizeAnalysisStatus(sa.status) ?? sa.status,
					externalId: sa.externalId,
					externalType: sa.externalType,
					packId: sa.packId,
					deferredMedia: sa.deferredMedia === true,
					_statusBecameInProgressAt: isInProgress(sa.status) ? (sa.updatedAt ? new Date(sa.updatedAt) : new Date()) : undefined
				});
			} else if (sa.status === AnalysisStatus.CANCELLED) {
				const row = rows.value.find((r) => r.id === sa.id);
				if (row && isStoreTerminalOlderThanListRow(row, sa, AnalysisStatus.CANCELLED)) {
					continue;
				}
				// An existing row was cancelled in the store — drop it so a cancelled report does not keep
				// showing as running while a stale getAnalyses response still reports RUNNING/CANCELLING.
				rows.value = rows.value.filter((r) => r.id !== sa.id);
			} else {
				// Update the status of an existing row from the store's fresher data.
				const row = rows.value.find((r) => r.id === sa.id);
				const storeStatus = normalizeAnalysisStatus(sa.status) ?? sa.status;
				if (row) {
					if (sa.deferredMedia !== undefined) {
						row.deferredMedia = sa.deferredMedia === true;
					}
					// The status-family endpoint's mediaCompleteAt is a different, looser semantic than the
					// results-family endpoint mapToRows uses (analysisSessions[0].mediaCompleteAt).
					// isInitialReportReady() itself no longer trusts mediaCompleteAt for WAITING_FOR_INPUT at
					// all, so this guard isn't needed to fix that miscategorization anymore — but row here
					// also feeds scribeStore.syncActiveAnalysesFromList() below, which writes row.mediaCompleteAt
					// back into the shared store. Without this guard, the looser value would round-trip into
					// state other components read too, so this stays to keep that shared value trustworthy.
					if (sa.mediaCompleteAt !== undefined && storeStatus !== AnalysisStatus.WAITING_FOR_INPUT) {
						row.mediaCompleteAt = sa.mediaCompleteAt;
					}
				}
				if (row && row.status !== storeStatus) {
					if (isStoreTerminalOlderThanListRow(row, sa, storeStatus)) {
						continue;
					}
					if (!isInProgress(row.status) && isInProgress(storeStatus)) {
						// LSP-15648: the list row is terminal (COMPLETED/FAILED/CANCELLED) while the store still
						// reports the same analysis as running. A completed report result is authoritative, and a
						// re-run always gets a NEW analysis id, so this is never a same-id restart. Trust the
						// terminal row and sync the store entry so the running indicator clears. This replaces the
						// brittle client-clock `updatedAt` heuristic — now that the store monitors the same
						// composite scope as the table, an SSE-stamped client clock can no longer mask completion.
						console.warn("[EntityReports] store reported running but the list row is terminal; trusting the terminal row", {
							analysisId: row.id,
							entityId: props.entity?.id,
							externalId: row.externalId,
							rowStatus: row.status,
							storeStatus
						});
						// Sync the store via an action (not a direct mutation) so it also invalidates any
						// in-flight composite refresh — otherwise an older refresh resolving with a stale
						// RUNNING status could revert this correction and re-strand the running indicator.
						const rowUpdatedAt = toDate(row.updatedAt);
						scribeStore.reconcileTerminalFromList(sa.id, row.status, rowUpdatedAt ?? undefined);
						continue;
					}
					row.status = storeStatus;
				}
			}
		}
	}

	function syncStoreFromActiveRows() {
		const rawActiveRows = rows.value.filter((row) => isInProgress(row.status));

		const activeAnalyses = rawActiveRows.map((row): AnalysisStatusResponseData => {
			return {
				id: row.id,
				externalType: row.externalType ?? props.entityType ?? "conversation",
				externalId: row.externalId ?? ((props.entity as Conversation | Project | undefined)?.id as string),
				externalSource: row._analysis?.externalSource ?? "contact",
				packId: row.packId,
				title: row.title,
				status: normalizeAnalysisStatus(row.status) ?? row.status,
				isRealTime: row.isRealTime,
				createdAt: row.createdAt as Date,
				updatedAt: (row.updatedAt ?? row.createdAt) as Date,
				mediaCompleteAt: row.mediaCompleteAt,
				deferredMedia: row.deferredMedia === true
			};
		});

		const previousDigest = digestAnalyses(scribeStore.currentAnalyses as AnalysisStatusResponseData[]);
		scribeStore.syncActiveAnalysesFromList(activeAnalyses);
		const nextDigest = digestAnalyses(scribeStore.currentAnalyses as AnalysisStatusResponseData[]);
		if (nextDigest !== previousDigest) {
			// This store mutation came from the list load itself. The table already has the same data,
			// so mark the digest observed and avoid immediately re-entering load() from the store watcher.
			lastStatusDigest.value = nextDigest;
		}
	}

	async function loadScheduledAnalyses() {
		const requestId = ++scheduledAnalysesRequestId.value;
		const entityId = props.entity?.id as string | undefined;
		// LSP-19608: shared by the isSuperseded check below AND every post-await gate, so "this request is
		// still the one anybody wants" is judged identically everywhere — including after unmount, when
		// neither requestId nor props.entity?.id would otherwise change to reflect that.
		const isCurrentRequest = () =>
			!isUnmounted && requestId === scheduledAnalysesRequestId.value && entityId === (props.entity?.id as string | undefined);

		if (!entityId || !props.entityType) {
			scheduledAnalyses.value = [];
			loadingScheduledAnalyses.value = false;
			return;
		}

		loadingScheduledAnalyses.value = true;

		try {
			// LSP-18409 (single-door): source the project's queued/omission rows from the OWNER-tenant
			// aggregate too; the queued/no_media filter below extracts exactly those from the merged list.
			// LSP-19608: a just-created conversation can 404 here briefly (backend still propagating it) —
			// retry a few times before giving up. A project-derived reader's 404 is structural (listSparks
			// is org-scoped, see LSP-17519), not transient, so don't waste retries on it.
			const isProjectDerivedReader = (props.entity as { accessVia?: string } | undefined)?.accessVia === "project";
			const sparkData = await readFreshEntity(
				() => (props.entityType === "project" ? api.getProjectSparks(entityId) : api.getSparks(entityId)),
				{
					isSuperseded: () => !isCurrentRequest(),
					tolerate404: !isProjectDerivedReader
				}
			);
			const rawScheduleData = (sparkData?.data ?? sparkData ?? []) as Array<{
				id: string;
				packId?: string;
				status: string;
				title?: string | null;
				createdAt: string;
				conversationId?: string;
				conversationName?: string | null;
				excludedDocuments?: ExcludedDocument[];
				isLegacySchedule?: boolean;
			}>;
			// LSP-18409: the project aggregate (getProjectSparks) also carries real Scribe analyses; keep
			// only the AnalysisSchedule placeholder rows (marker present) so a genuinely-queued analysis
			// isn't picked up here as a scheduled row. Conversation source is unchanged.
			const scheduleData =
				props.entityType === "project" ? rawScheduleData.filter((s) => s.isLegacySchedule !== undefined) : rawScheduleData;

			if (!isCurrentRequest()) {
				return;
			}

			scheduledAnalyses.value = scheduleData
				.filter((s) => {
					const status = s.status?.toLowerCase();
					return status === "queued" || (status === "no_media" && (s.excludedDocuments?.length ?? 0) > 0);
				})
				.map((s) => {
					const conversationId = s.conversationId ?? entityId;
					return {
						id: s.id,
						conversationId,
						conversationName: s.conversationName?.trim() || null,
						packId: s.packId ?? null,
						packName: s.title?.trim() || null,
						status: s.status,
						createdAt: s.createdAt,
						ownerLabel: getConversationOwnerLabel(conversationId),
						excludedDocuments: s.excludedDocuments,
						isLegacySchedule: !!s.isLegacySchedule
					} satisfies ScheduledAnalysisItem;
				});
		} catch (error) {
			// LSP-19608: isSuperseded stops a sleeping retry from firing again, but the pending promise
			// still rejects with the LAST REAL error (e.g. the original transient 404) — not a distinct
			// "superseded" error — so a request the user has since navigated away from lands here too.
			// Log at error level (Datadog-forwarded) only for the request still on screen; an abandoned
			// request's expected-transient failure is a warn, and its state is never cleared.
			if (isCurrentRequest()) {
				console.error("[EntityReports] Failed to load scheduled analyses:", error);
				scheduledAnalyses.value = [];
			} else {
				console.warn("[EntityReports] Abandoned scheduled-analyses request failed (superseded or unmounted):", error);
			}
		} finally {
			if (isCurrentRequest()) {
				loadingScheduledAnalyses.value = false;
			}
		}
	}

	function isScheduleVisibleInCurrentContext(schedule: ScheduledAnalysisItem): boolean {
		if (props.entityType === "project") {
			return additionalEntityIds.value.includes(schedule.conversationId);
		}

		return (props.entity?.id as string | undefined) === schedule.conversationId;
	}

	// Card-emit adapter: the mobile SparkyReportCard renders its Cancel
	// button on item.isScheduled, but _scheduledAnalysis is what the API
	// call needs. If a future code path leaves the flag set while the
	// schedule object is dropped (data merge bug, stale row, etc.), the
	// click would have been a silent no-op. Log the mismatch so a real
	// incident is recoverable without Datadog archaeology.
	function handleCardCancelSchedule(item: RowItem) {
		const schedule = item._scheduledAnalysis;
		if (!schedule) {
			console.warn("[EntityReports] cancelSchedule emitted for a card whose item is missing _scheduledAnalysis; ignoring", {
				itemId: item.id,
				isScheduled: item.isScheduled
			});
			return;
		}
		return handleCancelSchedule(schedule);
	}

	async function handleCancelSchedule(scheduleToCancel: ScheduledAnalysisItem) {
		if (!isScheduleVisibleInCurrentContext(scheduleToCancel)) {
			toast.error("That planned report is no longer available on this page. Please refresh and try again.");
			return;
		}

		// LSP-16345: explicit labels — affirmative "Cancel Planned Report", dismiss "Go Back".
		const confirm = (instance?.proxy as ComponentProxy | null | undefined)?.$root?.$confirm;
		const agreed = await confirm?.open?.(
			"Cancel Planned Report?",
			"Are you sure you want to cancel this planned report?",
			false,
			"Cancel Planned Report",
			"Go Back",
			true
		);
		if (!agreed) return;

		cancellingScheduleIds.add(scheduleToCancel.id);
		try {
			if (scheduleToCancel.isLegacySchedule) {
				// Legacy row — cancel via AnalysisSchedule (not yet in scribe)
				await api.cancelLegacySchedule(scheduleToCancel.conversationId, scheduleToCancel.id);
			} else {
				// New spark — cancel via scribe; only remove from UI if scribe confirms cancellation
				const result = await api.cancelSpark(scheduleToCancel.conversationId, scheduleToCancel.id);
				if (!result?.cancelled) {
					toast.error("The planned report could not be cancelled. Please try again.");
					return;
				}
			}
			scheduledAnalyses.value = scheduledAnalyses.value.filter((schedule) => schedule.id !== scheduleToCancel.id);
			removedAnalysisIds.add(scheduleToCancel.id);
			// For a deferred spark, the schedule was merged onto its QUEUED scribe row (same id ==
			// analysisId), which lives in rows.value — removing it from scheduledAnalyses alone would
			// leave that row visible (stuck) until the next poll. A cancelled planned report should
			// disappear immediately (matches the non-deferred behavior and prod), so drop the merged
			// row from rows.value too.
			rows.value = rows.value.filter((row) => row.id !== scheduleToCancel.id);
			// The planned cancel already completed, so drop the analysis from
			// the monitored set entirely — QUEUED counts as "awaiting report" and would otherwise keep
			// the Sparky popup animating until the next poll. removeAnalysis matches the "cancelled"
			// toast (gone, not transitional); no-op if it isn't being monitored.
			scribeStore.removeAnalysis(scheduleToCancel.id);
			toast.success("The planned report has been cancelled.");
		} catch (error) {
			console.error("[EntityReports] Failed to cancel analysis schedule:", error);
			toast.error(couldNot("cancel the planned report"));
		} finally {
			cancellingScheduleIds.delete(scheduleToCancel.id);
		}
	}

	// LSP-16433: fetch the oversized-document omissions for the reports currently shown and index them by
	// Scribe analysisId (=== report row id) — the only reliable bridge, since Scribe results carry no
	// packId/externalId — so combinedRows can attach each omission to its exact report row.
	async function loadExcludedDocuments(scannedRows: RowItem[], requestId: number) {
		const analysisIds = Array.from(new Set(scannedRows.map((row) => row.id).filter(Boolean))) as string[];
		if (!analysisIds.length) {
			// Only clear if this is still the latest load — a stale empty-row pass must not wipe omissions a
			// newer load already applied.
			if (requestId === loadRequestId.value) {
				excludedDocsByAnalysisId.value = new Map();
			}
			return;
		}
		// LSP-18409 (review F6): project omission ledgers live under the OWNER org, so the caller-scoped
		// getExcludedDocumentsByAnalyses would miss them for a cross-org editor. The owner-tenant
		// getProjectSparks aggregate already carries excludedDocuments on each row, so build the chip map
		// from the rows for projects. Conversations keep the caller-scoped fetch.
		if (props.entityType === "project") {
			if (requestId !== loadRequestId.value) return;
			const byAnalysisId = new Map<string, ExcludedDocument[]>();
			for (const row of scannedRows) {
				if (row.excludedDocuments?.length && !byAnalysisId.has(row.id)) {
					byAnalysisId.set(row.id, row.excludedDocuments);
				}
			}
			excludedDocsByAnalysisId.value = byAnalysisId;
			return;
		}
		try {
			const excluded = await api.getExcludedDocumentsByAnalyses(analysisIds);
			if (requestId !== loadRequestId.value) return;
			const byAnalysisId = new Map<string, ExcludedDocument[]>();
			for (const entry of excluded) {
				if (entry.analysisId && !byAnalysisId.has(entry.analysisId)) {
					byAnalysisId.set(entry.analysisId, entry.excludedDocuments);
				}
			}
			excludedDocsByAnalysisId.value = byAnalysisId;
		} catch (error) {
			// Non-fatal: the report still renders, just without the omission notice. Log so a future
			// "why is the excluded-documents notice missing?" triage is a Datadog lookup, not archaeology.
			console.warn("[EntityReports] Failed to load excluded documents for the report list.", error);
			// Only clear if this is still the latest load — a superseded request that fails must not wipe the
			// omissions a newer load already applied (stale-fetch race).
			if (requestId === loadRequestId.value) {
				excludedDocsByAnalysisId.value = new Map();
			}
		}
	}

	async function load(options?: { silent?: boolean }) {
		const silent = options?.silent === true;
		const requestId = ++loadRequestId.value;

		if (!props?.entity?.id || !props?.entityType) {
			rows.value = [];
			loading.value = false;
			return;
		}
		// Background refreshes (e.g. polling, visibility re-show) keep `loading`
		// false so the indeterminate progress bar doesn't flash on top of the
		// data table every ACTIVE_REPORT_POLL_MS.
		if (!silent) {
			loading.value = true;
		}

		try {
			let newRows: RowItem[];
			if (props.entityType === "project") {
				// LSP-18409 (single-door): list the OWNER tenant's aggregate (project + its conversations)
				// via the server so the owner and an external editor see the SAME reports — replacing the
				// browser-direct getAnalyses + getAnalysesBulk (caller tenant). Queued/omission placeholder
				// rows are loaded separately by loadScheduledAnalyses from the same aggregate, so keep only
				// real Scribe analyses here, matching what getAnalyses returned for the conversation path.
				const aggregate = await api.getProjectSparks(props.entity.id);
				if (requestId !== loadRequestId.value) return;
				const scribeItems = ((aggregate?.data ?? []) as AnalysisResult[]).filter(
					(item) => !isLegacyScheduleItem(item as { isLegacySchedule?: boolean })
				);
				newRows = mapToRows(scribeItems).filter((row) => !removedAnalysisIds.has(row.id));
			} else {
				const result = await getAnalyses("contact", "conversation", props.entity.id);
				if (requestId !== loadRequestId.value) return;
				newRows = mapToRows(result.data || []).filter((row) => !removedAnalysisIds.has(row.id));
			}

			if (requestId !== loadRequestId.value) return;

			rows.value = newRows;
			// LSP-17976: contact-api answers an archived user's report list with an empty array.
			if (newRows.length === 0) void scribeAccess.confirmArchived();

			await loadExcludedDocuments(newRows, requestId);
			if (requestId !== loadRequestId.value) return;

			mergeStoreAnalyses();
			syncStoreFromActiveRows();

			rows.value.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

			if (props.subEntityId) {
				// Open from combinedRows (not rows.value) so a hash deep-link opens the report WITH its
				// excludedDocuments merged in — otherwise the dialog shows no omission notice even though the
				// list chip would (LSP-16433: rows.value carries no omissions; combinedRows merges them).
				const analysis = combinedRows.value.find((a) => a.id === props.subEntityId);
				if (analysis) {
					view(analysis);
				}
			}
		} catch (e) {
			if (requestId !== loadRequestId.value) {
				console.debug("[EntityReports] dropped error from superseded load", { requestId, silent });
				return;
			}
			console.error(e);
			// On a silent (background) refresh we must NOT clear rows or flip
			// `loadFailed`, otherwise one transient network blip during polling
			// replaces the whole reports section with the empty-state fallback.
			if (!silent) {
				loadFailed.value = true;
				rows.value = [];
				// LSP-17976: an archived user's failure gets the access notice, not the
				// owning-organization fallback.
				void scribeAccess.confirmArchived();
			}
		} finally {
			// Always clear `loading` for the winning request, even when it ran as
			// silent — otherwise a silent load that supersedes an in-flight non-silent
			// load leaves the spinner stuck (the non-silent finally is skipped for the
			// stale requestId).
			if (requestId === loadRequestId.value) {
				loading.value = false;
			} else {
				console.debug("[EntityReports] load finally skipped for losing request", {
					requestId,
					winning: loadRequestId.value,
					silent
				});
			}
		}
	}

	function emitCreate() {
		emit("create");
	}

	async function view(item: RowItem) {
		if (item.isScheduled) return;
		const target = { reportId: item.id, entityType: props.entityType, entityId: props.entity.id as string };
		try {
			// Allow viewing if completed OR initial report ready OR partial result
			if (isReportViewable(item)) {
				loadingAnalysis.value = true;
				// LSP-18409 (single-door): open project reports through the owner-tenant proxy so an external
				// editor reads exactly what the owner sees; conversations keep the browser-direct read.
				const result =
					props.entityType === "project"
						? await api.getProjectSparkAnalysis(props.entity.id as string, item.id)
						: await getAnalysis(item.id);
				// LSP-21931: Scribe's getAnalysis resolves null on a 404 rather than rejecting — opening the
				// dialog on it showed nothing at all.
				if (!result) {
					notifyReportUnavailable(toast, target, "empty read");
					return;
				}
				selectedAnalysis.value = result as AnalysisResult;
				selectedExcludedDocuments.value = item.excludedDocuments ?? [];
				await nextTick(() => {
					reportVisible.value = true;
				});
				return;
			}

			if (item.status === AnalysisStatus.RUNNING || item.status === AnalysisStatus.QUEUED || item.status === AnalysisStatus.PENDING) {
				toast.info("This report is still running. Please check back in a few minutes.");
				return;
			}
			if (item.status === AnalysisStatus.CANCELLING) {
				toast.info("This report is being cancelled.");
				return;
			}
			if (item.status === AnalysisStatus.CANCELLED) {
				toast.info("This report was cancelled.");
				return;
			}
			if (item.status === AnalysisStatus.FAILED) {
				return;
			}
		} catch (err) {
			reportVisible.value = false;
			notifyReportOpenFailed(toast, target, err);
		} finally {
			loadingAnalysis.value = false;
		}
	}

	async function handleCancel(item: RowItem) {
		const currentItem = combinedRows.value.find((candidate) => candidate.id === item.id);
		// LSP-20160: a second activation while the confirm is open, the request is in flight, or the row
		// is already CANCELLING must not open another confirm or send another cancel. The disabled button
		// only blocks pointer input; emits and programmatic calls reach this handler regardless.
		const cancelInProgressReason = confirmingCancelIds.has(item.id)
			? "confirm-open"
			: cancellingIds.has(item.id) || (currentItem && isScheduleCancelInFlight(currentItem))
				? "request-in-flight"
				: currentItem && isCancellingAnalysisStatus(currentItem.status)
					? "status-cancelling"
					: null;
		if (cancelInProgressReason) {
			console.warn("[EntityReports] Ignoring report cancellation because a cancel is already in progress", {
				analysisId: item.id,
				entityId: props.entity?.id,
				status: currentItem?.status ?? item.status,
				reason: cancelInProgressReason
			});
			return;
		}
		if (!currentItem) {
			console.warn("[EntityReports] Ignoring stale report cancellation because the report is no longer active", {
				analysisId: item.id,
				entityId: props.entity?.id
			});
			return;
		}
		if (!isCancellableAnalysisStatus(currentItem.status)) {
			console.warn("[EntityReports] Ignoring stale report cancellation because the report is no longer cancellable", {
				analysisId: currentItem.id,
				entityId: props.entity?.id,
				status: currentItem.status
			});
			return;
		}

		// A spark analysis can still carry its planned AnalysisSchedule row after Scribe has moved it
		// out of the deferred-for-media state. At that point the merged row is cancellable as a running
		// report, but cancelling Scribe alone would leave the AnalysisSchedule row Scheduled, so listSparks
		// resurfaces it and dispatch re-patches on the next media event.
		// Route any row that carries a schedule through the schedule-aware path, which cancels scribe
		// (cancelSpark) AND removes the schedule row (or cancelLegacySchedule for rows not yet in scribe).
		if (currentItem._scheduledAnalysis) {
			// LSP-20160: the schedule path opens its own confirm before it records cancellingScheduleIds, so
			// hold the row in confirmingCancelIds for the whole hand-off — a second activation is a no-op.
			confirmingCancelIds.add(currentItem.id);
			try {
				await handleCancelSchedule(currentItem._scheduledAnalysis);
			} finally {
				confirmingCancelIds.delete(currentItem.id);
			}
			return;
		}

		// LSP-16345: use explicit labels via $confirm.open so the affirmative reads "Cancel Report"
		// (not the generic deleteOpen "Delete") and the dismiss reads "Go Back" (not a confusing
		// second button labelled "Cancel").
		const confirm = (instance?.proxy as ComponentProxy | null | undefined)?.$root?.$confirm;
		confirmingCancelIds.add(currentItem.id);
		let agreed: boolean | undefined;
		try {
			agreed = await confirm?.open?.(
				"Cancel Report?",
				"Are you sure you want to cancel this report?",
				false,
				"Cancel Report",
				"Go Back",
				true
			);
		} finally {
			confirmingCancelIds.delete(currentItem.id);
		}
		if (!agreed) return;

		cancellingIds.add(currentItem.id);
		try {
			const result = await cancelSparkForRow(currentItem);
			if (!result?.cancelled) {
				toast.error("The report could not be cancelled. Please try again.");
				return;
			}
			scribeStore.markAnalysisCancelling(currentItem.id);
			const row = rows.value.find((candidate) => candidate.id === currentItem.id);
			if (row) {
				row.status = AnalysisStatus.CANCELLING;
			}
			toast.success("Report cancellation requested.");
		} catch (error) {
			const message = (error as Error | { message?: string } | null | undefined)?.message;
			toast.error(message || "Failed to cancel the report.");
		} finally {
			cancellingIds.delete(currentItem.id);
		}
	}

	async function handleFinalize(item: RowItem) {
		if (!item.sessionId) {
			toast.error("Unable to finalize: session information not available.");
			return;
		}

		const confirm = (instance?.proxy as ComponentProxy | null | undefined)?.$root?.$confirm;
		const agreed = await confirm?.open?.(
			"Finalize Real-Time Report",
			"This will finalize the real-time report with its current inventory. Any ongoing analysis will be stopped. Are you sure?",
			false,
			"Finalize",
			"Cancel",
			false
		);
		if (!agreed) return;

		finalizingIds.add(item.id);
		let succeeded = false;
		try {
			await finalizeRealtimeAnalysis(item.sessionId, item.id);
			toast.success("Report finalization requested. It may take a moment to complete.");
			succeeded = true;
		} catch (error) {
			const message = (error as Error | { message?: string } | null | undefined)?.message;
			toast.error(message || "Failed to finalize the report.");
		} finally {
			finalizingIds.delete(item.id);
		}
		// Only reload if finalization succeeded
		if (succeeded) {
			try {
				await load();
			} catch {
				// Silently ignore reload errors - finalization already succeeded
			}
		}
	}

	async function remove(item: RowItem) {
		const ask = (instance?.proxy as ComponentProxy | null | undefined)?.$root?.$confirm?.deleteOpen;
		const agreed = await ask?.("Delete Report", "This will delete the report.");
		if (!agreed) return;

		// Route delete through contact-api (deleteSpark) rather than scribe directly. contact-api deletes
		// the scribe analysis and removes the matching AnalysisSchedule row, so a
		// completed deferred spark leaves no stale row behind. A scribe-direct delete would orphan it.
		try {
			await deleteSparkForRow(item);
			removedAnalysisIds.add(item.id);
			scribeStore.removeAnalysis(item.id);
			rows.value = rows.value.filter((x) => x.id !== item.id);
			// If this analysis row was merged with a planned AnalysisSchedule entry (deferred spark that
			// has since run), drop that schedule too. Otherwise removing only the analysis row unmerges
			// the still-present schedule, which then flashes back as a phantom "Scheduled" row until the
			// next poll re-fetches and drops it.
			if (item._scheduledAnalysis) {
				const scheduleId = item._scheduledAnalysis.id;
				scheduledAnalyses.value = scheduledAnalyses.value.filter((schedule) => schedule.id !== scheduleId);
			}
			toast.success("The report has been deleted.");
		} catch (error) {
			const message = (error as Error | { message?: string } | null | undefined)?.message;
			toast.error(message || "Failed to delete the report.");
		}
	}

	function handleReportUpdated(updated: AnalysisResult) {
		selectedAnalysis.value = updated;
		const index = rows.value.findIndex((row) => row.id === updated.id);
		if (index === -1) return;

		const previous = rows.value[index];
		const [mapped] = mapToRows([updated]);
		rows.value[index] = {
			...previous,
			...mapped,
			_statusBecameInProgressAt: previous._statusBecameInProgressAt ?? mapped._statusBecameInProgressAt,
			_scheduledAnalysis: previous._scheduledAnalysis
		};
	}

	function handleDialogInput(value: boolean) {
		if (!value) {
			// Dialog is closing or has closed.
			const currentHash = route.hash;

			if (currentHash && currentHash.startsWith(`#${TabIds["sparkys-reports"]}/`)) {
				// If the hash matches '#sparkys-reports/<id>', change it to '#sparkys-reports'.
				router.replace(`#${TabIds["sparkys-reports"]}`);
			}
		}
	}

	// --- Scribe monitoring wiring (start/stop and reactive reloads) ---
	const scribeStore = useScribeStore();

	// LSP-20159: "Starting your report…" placeholders the run flow recorded for THIS entity. A resolved one
	// (its create request returned) stays visible until its real row is in the list, so there is no gap
	// between the request returning and the next list refresh.
	const visibleStartingReports = computed(() => {
		const listedIds = new Set(combinedRows.value.map((row) => row.id));
		return scribeStore.startingReports.filter(
			(report) =>
				report.externalType === props.entityType &&
				report.externalId === props.entity?.id &&
				!(report.analysisId && listedIds.has(report.analysisId))
		);
	});
	// LSP-17976: the archived-access notice replaces only an empty or failed list, never loaded reports.
	const hasAnyReportRows = computed(
		() => activeRows.value.length > 0 || completedRows.value.length > 0 || visibleStartingReports.value.length > 0
	);

	// Retire a resolved placeholder once its row has reached the list.
	watch([() => scribeStore.startingReports, combinedRows], () => {
		const listedIds = new Set(combinedRows.value.map((row) => row.id));
		for (const report of scribeStore.startingReports) {
			if (report.analysisId && listedIds.has(report.analysisId)) {
				scribeStore.removeStartingReport(report.clientId);
			}
		}
	});

	async function startMonitoringForProps(): Promise<void> {
		const id = (props?.entity as Conversation | Project)?.id as string | undefined;
		const type: "project" | "conversation" | undefined =
			props?.entityType === "project" ? "project" : props?.entityType ? "conversation" : undefined;
		if (id && type) {
			// LSP-15648: a project page aggregates child conversation reports, so monitor the composite
			// scope. EntityDetailPage also seeds this at the page level (so re-navigation works even before
			// this lazy tab mounts); the store ignores an empty child set on a same-primary call, so an early
			// mount here (before additionalEntities load) cannot clobber the page-seeded scope.
			await scribeStore.startMonitoring(id, type, undefined, undefined, type === "project" ? additionalEntityIds.value : undefined);
		}
	}

	// Track the last seen running-state set to avoid redundant loads.
	const lastStatusDigest = ref<string>("");
	function digestAnalyses(list: AnalysisStatusResponseData[] | undefined): string {
		if (!list || list.length === 0) return "";
		// Use a stable digest of fields that affect the table's active/scheduled/viewable state.
		const pairs = list
			.map((a) => ({
				id: a.id,
				status: a.status,
				deferredMedia: a.deferredMedia === true,
				mediaCompleteAt: toDate(a.mediaCompleteAt)?.toISOString() ?? null
			}))
			.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
		return JSON.stringify(pairs);
	}

	let delayedRefreshTimeout: ReturnType<typeof setTimeout> | null = null;

	// Fallback polling in case SSE `analysis:updated` events are missed;
	// otherwise active reports remain stuck on "Running" in the UI.
	const ACTIVE_REPORT_POLL_MS = 20_000;
	let activeReportPollInterval: ReturnType<typeof setInterval> | null = null;

	function startActiveReportPolling() {
		if (activeReportPollInterval) {
			console.warn("[EntityReports] startActiveReportPolling called with interval already armed");
			return;
		}
		activeReportPollInterval = setInterval(async () => {
			// Pause while the tab is hidden — handleVisibilityChange catches up on return.
			if (document.visibilityState !== "visible") return;
			if (pollableRows.value.length === 0) {
				stopActiveReportPolling();
				return;
			}

			// The "running dog" (EntityActions -> scribeStore.anyAnalysisIsRunning) is
			// driven SOLELY by scribeStore.currentAnalyses, which load() never refreshes.
			// So if an `analysis:updated` SSE event is missed, load() heals the table but
			// the dog spins forever. Refresh the store here too — the currentAnalyses
			// watcher reloads the table on any status change.
			//
			// LSP-15648: this is timer-driven polling, so honour the VITE_DISABLE_SCRIBE_POLLING kill
			// switch. refreshAnalyses(true) forces past only the 30s cap, not the kill switch — and a
			// project refresh now fans out to every child, so bypassing the switch here would multiply
			// load on a Scribe API we were told to stop hitting. SSE-driven refreshes still run.
			let statusRefreshClearedRunning = false;
			if (!scribeStore.pollingDisabled) {
				const wasRunning = scribeStore.anyAnalysisIsRunning;
				await scribeStore.refreshAnalyses(true);
				statusRefreshClearedRunning = wasRunning && !scribeStore.anyAnalysisIsRunning;
			}

			await load({ silent: true });
			if (statusRefreshClearedRunning) {
				// Silent-failure diagnostic: the fallback poll — not SSE — is what cleared
				// the running state, meaning an `analysis:updated` event was missed. Log it
				// so a recurrence is traceable without Datadog archaeology.
				console.warn("[EntityReports] fallback poll recovered a completion the SSE stream missed", {
					entityType: props.entityType,
					entityId: props.entity?.id
				});
			}
		}, ACTIVE_REPORT_POLL_MS);
	}

	function stopActiveReportPolling() {
		if (activeReportPollInterval) {
			clearInterval(activeReportPollInterval);
			activeReportPollInterval = null;
		}
	}

	watch(
		() => pollableRows.value.length > 0,
		(hasActive) => {
			if (hasActive) {
				startActiveReportPolling();
			} else {
				stopActiveReportPolling();
			}
		}
	);

	function handleVisibilityChange() {
		if (document.visibilityState !== "visible") return;
		// LSP-15648: the visibility catch-up is a poll-equivalent status refresh, so honour the
		// VITE_DISABLE_SCRIBE_POLLING kill switch — composite monitoring now fans it out to every child
		// scope. SSE-driven refreshes still run when disabled.
		if (scribeStore.pollingDisabled) return;
		if (pollableRows.value.length === 0) {
			console.debug("[EntityReports] visibility refresh skipped — no active rows");
			return;
		}
		scribeStore.refreshAnalyses(true).catch((e) => {
			console.error("[EntityReports] visibility refresh failed", e);
		});
	}

	let initializationRequestId = 0;
	async function initializeReports(): Promise<void> {
		const requestId = ++initializationRequestId;
		loading.value = true;
		await startMonitoringForProps();
		if (requestId !== initializationRequestId || isUnmounted) return;
		lastStatusDigest.value = digestAnalyses(scribeStore.currentAnalyses as AnalysisStatusResponseData[]);
		await Promise.all([load(), loadScheduledAnalyses()]);
	}

	onMounted(() => {
		void initializeReports();

		nowInterval = setInterval(() => {
			now.value = Date.now();
		}, 1000);

		// Keep the existing safety refresh for a status change that lands just after initialization.
		// The awaited monitoring refresh above makes the first paint correct; this remains a silent fallback.
		delayedRefreshTimeout = setTimeout(() => {
			load({ silent: true });
		}, 5000);

		document.addEventListener("visibilitychange", handleVisibilityChange);
	});

	onUnmounted(() => {
		initializationRequestId++;
		scribeStore.stopMonitoring();
		if (nowInterval) {
			clearInterval(nowInterval);
			nowInterval = null;
		}
		if (delayedRefreshTimeout) {
			clearTimeout(delayedRefreshTimeout);
			delayedRefreshTimeout = null;
		}
		stopActiveReportPolling();
		document.removeEventListener("visibilitychange", handleVisibilityChange);
		isUnmounted = true;
	});

	// Restart monitoring and reload when the bound entity changes
	watch(
		() => [props.entity?.id, props.entityType, additionalEntityIdsDigest.value],
		() => {
			scribeStore.stopMonitoring();
			void initializeReports();
		}
	);

	// Live-refresh with precise status-change detection (covers both running flag toggles and individual status changes)
	watch(
		() => scribeStore.currentAnalyses,
		(list) => {
			const digest = digestAnalyses(list as AnalysisStatusResponseData[]);
			if (digest !== lastStatusDigest.value) {
				lastStatusDigest.value = digest;
				// SSE-driven reloads run on already-rendered data; keep them silent
				// so users don't see the loading bar flash on every status update.
				load({ silent: true });
			}
		},
		{ deep: true }
	);
</script>
<style lang="scss" scoped>
	.entity-reports-wrapper {
		max-height: calc(100vh - 220px);
		overflow-y: auto;
		scrollbar-width: thin;

		/* LSP-16833: on smAndDown, EntityDetailPage deliberately leaves its card height
		   unpinned (see the LSP-16462 note there) so the DOCUMENT is the page's only
		   scroller. Clamping this wrapper to a "100vh minus the chrome above me" guess
		   added a SECOND, nested scrollbar inside that scrolling page: the report list
		   and the page both scrolled, and a flick went to whichever container the touch
		   landed in, so scrolling read as glitchy/stuck. Hand the list to the document
		   scroll instead of guessing the parent's height. This also repairs landscape,
		   which fell through to the desktop 220px reservation and squeezed the list into
		   ~170px on a phone (the same shape of bug LSP-13368 patched in portrait). */
		@media #{map.get($display-breakpoints, 'sm-and-down')} {
			max-height: none;
			overflow-y: visible;
		}
	}

	.entity-reports :deep(table) {
		table-layout: fixed;
		width: 100%;
		/* LSP-16766: Session (130px) + Created At/Elapsed (100px) + actions (60-110px) are
		   fixed-width, so in the narrow right-hand reports panel the width:100% table squeezes
		   the flexible Name column toward 0 and its header abuts "Session" ("NameSession").
		   A min-width keeps every column readable; when the panel is narrower than this the
		   table overflows and the wrapper below scrolls horizontally to reveal Name instead. */
		min-width: 480px;
	}

	/* LSP-16766: scope the horizontal scroll to the table's own wrapper (Vuetify's
	   .v-table__wrapper) so only the table scrolls, not the panel header/actions. */
	.entity-reports :deep(.v-table__wrapper) {
		overflow-x: auto;
	}

	.entity-reports :deep(td),
	.entity-reports :deep(th) {
		padding: 0 !important;
	}

	.entity-reports :deep(td) {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.entity-reports :deep(td .d-flex) {
		white-space: nowrap;
		overflow: visible;
	}

	/* LSP-15914: without min-width:0 the text-truncate span fills the entire cell
	   and the Running / Scheduled chip overlaps the report name. Give the name
	   span the flex shrink permissions needed for text-overflow ellipsis to work
	   inside the flex parent. */
	.entity-reports :deep(.report-name-text) {
		min-width: 0;
		flex: 1 1 auto;
	}

	@media #{map.get($display-breakpoints, 'sm-and-down')} {
		/* LSP-15914 (mobile follow-up): allow the chip to drop below the name
		   instead of pushing it off-screen when the title is wider than the cell. */
		.entity-reports :deep(.report-name-cell) {
			flex-wrap: wrap;
			row-gap: 2px;
		}
	}

	.status-chip-scheduled {
		background-color: rgba(255, 184, 0, 0.14) !important;
		color: rgba(233, 168, 0, 1) !important;
	}

	.status-chip-processing {
		background-color: rgba(var(--v-theme-primary), 0.14) !important;
		color: rgb(var(--v-theme-primary)) !important;
	}
</style>
